"use client";

import { Suspense, useState } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useCurrentUser } from "@/lib/hooks/useCurrentUser";
import { useTitle } from "@/lib/hooks/useTitle";
import { GroupDiscovery } from "@/components/GroupDiscovery";
import { GroupExploreMap } from "@/components/GroupExploreMap";
import { lookupLocation, locationLabel } from "@/lib/locations";

export default function GrupperPage() {
  return (
    <Suspense fallback={null}>
      <GrupperContent />
    </Suspense>
  );
}

/**
 * `?location=<token>` IS THE MAP'S DRILL-DOWN.
 *
 * A marker on the explore map is a PLACE with a group count, and clicking it
 * lands here. `GET /groups?location=` runs the predicate that produced the
 * count (§2.14), so the number on the marker and the length of this list are
 * one query apart and cannot disagree.
 */
function GrupperContent() {
  useTitle("Grupper");
  const { data: currentUser } = useCurrentUser();
  const router = useRouter();
  const searchParams = useSearchParams();
  const location = searchParams.get("location");
  const locationName = location ? lookupLocation(location) : null;
  const [showMap, setShowMap] = useState(false);

  return (
    <div className="bg-surface rounded-xl border border-border p-4 sm:p-6">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">Udforsk grupper</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Find et fællesskab der passer til dig
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => setShowMap((v) => !v)}
            aria-pressed={showMap}
            title="Vis grupper på kort"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
              showMap
                ? "bg-primary-muted border-indigo-300 dark:border-indigo-700 text-primary-muted-foreground"
                : "border-border text-muted-foreground hover:bg-surface-2"
            }`}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            <span className="hidden sm:inline">Kort</span>
          </button>
          {currentUser && (
            <Link
              href="/grupper/opret"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              <span className="hidden sm:inline">Opret gruppe</span>
              <span className="sm:hidden">Opret</span>
            </Link>
          )}
        </div>
      </div>
      {showMap && (
        <div className="mb-4 h-[420px] rounded-lg overflow-hidden border border-border">
          <GroupExploreMap />
        </div>
      )}
      {location && (
        <div className="mb-4 flex items-center gap-2 text-sm">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-muted px-3 py-1 text-primary-muted-foreground">
            {locationName ? locationLabel(locationName) : location}
          </span>
          <button
            type="button"
            onClick={() => router.push("/grupper")}
            className="text-primary underline"
          >
            Ryd filter
          </button>
        </div>
      )}
      <GroupDiscovery location={location ?? undefined} />
    </div>
  );
}
